import { array } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismTooltipFooter,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-difference-row';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-row';
import {
  INVALID_RESERVATIONS_MESSAGE,
  KPIS,
  REVENUE_INSIGHT_METRICS,
} from 'lumen/utils/constants/ri-constant-values';
import roundSegmentationValue from 'lumen/utils/revenue-insight/round-segmentation-value';
import { segmentationValueFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';
import {
  changeValueFormat,
  changeValuePercentageSignFormat,
} from 'lumen/utils/revenue-insight/value-formatting';

interface HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsightSignature {
  Args: {
    data: unknown;
    hideTarget?: boolean;
    hideTitle?: boolean;
    kpiCategory?: unknown;
    kpis: unknown;
    metricKey: unknown;
    section?: unknown;
  };
}

export default class HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsight extends Component<HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsightSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  @computed(
    'args.data.parentId',
    'riSelections.{isBookingsOnlyView,isMultiProperty}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowDiscrepancy() {
    const { isBookingsOnlyView, isMultiProperty } = this.riSelections;

    if (!isBookingsOnlyView) {
      return false;
    }

    // @ts-expect-error: Incorrect type
    const isTotalBookedKPI = !this.args.data?.parentId;

    if (isTotalBookedKPI) {
      return true;
    }

    const isTransientGroupFilterEnabled =
      isMultiProperty ||
      this.access.hasFeature('revenueinsight.transient_group_filter');

    return isTransientGroupFilterEnabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowDiscrepancyReferenceValue() {
    if (!this.canShowDiscrepancy) {
      return false;
    }

    return (
      this.discrepancyReferenceValue !== null &&
      this.discrepancyReferenceValue !== undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowDiscrepancyValue() {
    if (!this.canShowDiscrepancy) {
      return false;
    }

    return (
      this.discrepancyValue !== null && this.discrepancyValue !== undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get discrepancyReferenceValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.tooltipValues.referenceValueDiffReservations?.[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.metricKey
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get discrepancyValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.tooltipValues.valueDiffReservations?.[this.args.metricKey];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDisceprancyReferencePercentage() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const percentage =
      // @ts-expect-error: Incorrect type
      this.tooltipValues.referenceValueDiffReservationsPercentage?.[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.metricKey
      ];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return changeValuePercentageSignFormat(percentage, this.intl);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDiscrepancyPercentage() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const percentage =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.tooltipValues.valueDiffReservationsPercentage?.[this.args.metricKey];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return changeValuePercentageSignFormat(percentage, this.intl);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDiscrepancyReferenceValue() {
    const usePercentages = this.singleKPI === KPIS.OCCUPANCY;

    return changeValueFormat(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.discrepancyReferenceValue,
      usePercentages,
      this.intl,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDiscrepancyValue() {
    const usePercentages = this.singleKPI === KPIS.OCCUPANCY;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return changeValueFormat(this.discrepancyValue, usePercentages, this.intl);
  }

  @computed('args.{data.calculatedValues.forecastReference,metricKey}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasForecastReference() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const forecastReferenceValue =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.data?.calculatedValues?.forecastReference[this.args.metricKey];

    return (
      forecastReferenceValue !== null && forecastReferenceValue !== undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPickup() {
    // @ts-expect-error: Incorrect type
    return this.args.data?.segmentationId === RI_BREAKDOWNS.PICKUP;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPickupAndPaceIncomplete() {
    const metricsToCheck = [...this.thisYearMetrics, ...this.lastYearMetrics];

    return metricsToCheck.some(
      (metric) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.tooltipValues[metric].message === INVALID_RESERVATIONS_MESSAGE,
    );
  }

  @computed('args.data.segmentationId')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPickUpOrPaceSegmentationItem() {
    return (
      // @ts-expect-error: Incorrect type
      this.args.data.segmentationId === RI_BREAKDOWNS.PICKUP ||
      // @ts-expect-error: Incorrect type
      this.args.data.segmentationId === RI_BREAKDOWNS.PACE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTargetActive() {
    return this.args.hideTarget ? false : this.riSelections.isTargetActive;
  }

  @computed('args.kpis.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiTitles() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.kpis.map((kpi) => this.intl.t(`revenue_insight.${kpi}`));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastYearMetrics() {
    const metrics = [REVENUE_INSIGHT_METRICS.REFERENCE];

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.showPickupLYinThisYear &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      // @ts-expect-error: Incorrect type
      metrics.push(REVENUE_INSIGHT_METRICS.CANCELLATION_REFERENCE_OR_FILTERED);
    }

    return metrics;
  }

  /**
   * For last year, in case of cancellations we want to see the gross booking numbers in the tooltips
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastYearValueMetrics() {
    const metrics = [...this.lastYearMetrics];

    if (this.showGrossValues) {
      // @ts-expect-error: Incorrect type
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_REFERENCE;
    }

    return metrics;
  }

  @computed(
    'args.data.segmentationId',
    'riSelections.{isCancellationsOnlyView,isTargetActive,isSingleProperty}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showGrossValues() {
    const { isCancellationsOnlyView, isTargetActive, isSingleProperty } =
      this.riSelections;

    // @ts-expect-error: Incorrect type
    const isPickup = this.args.data?.segmentationId === RI_BREAKDOWNS.PICKUP;

    // Only show gross numbers in case of cancellations view or pickup (never in budget view)
    return (
      isSingleProperty &&
      (isCancellationsOnlyView || (isPickup && !isTargetActive))
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return (
      !this.riSelections.isCancellationsOnlyView && this.hasForecastReference
    );
  }

  @computed('showPickupLY', 'args.data.segmentationId')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLYinThisYear() {
    if (!this.showPickupLY) {
      return false;
    }

    // @ts-expect-error: Incorrect type
    return this.args.data?.segmentationId === RI_BREAKDOWNS.PACE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTooltipDifferenceRow() {
    return this.riSelections.isBookingsOnlyView && !this.isPickup;
  }

  @computed('args.kpis.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get singleKPI() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.kpis?.[0];
  }

  @computed('kpiTitles.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get singleKpiTitle() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.kpiTitles?.[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetChange() {
    return [`${this.targetValue[0]}Change`];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetValue() {
    if (this.riSelections.isBudgetActive) {
      return ['budget'];
    }

    if (this.riSelections.isForecastActive) {
      return ['forecast'];
    }

    return [''];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get thisYearMetrics() {
    if (this.showPickupLYinThisYear) {
      return ['forecastReference'];
    }

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      return [
        REVENUE_INSIGHT_METRICS.BOB,
        REVENUE_INSIGHT_METRICS.CANCELLATION_BOB_OR_FILTERED,
      ];
    }

    return [REVENUE_INSIGHT_METRICS.BOB];
  }

  /**
   * For this year, in case of cancellations we want to see the gross booking numbers in the tooltips
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get thisYearValueMetrics() {
    const metrics = [...this.thisYearMetrics];

    if (this.showGrossValues && metrics[0] === REVENUE_INSIGHT_METRICS.BOB) {
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_BOB;
    }

    return metrics;
  }

  @computed('args.data.{segmentationId,segmentationValue,displayValue}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipTitle() {
    // @ts-expect-error: Incorrect type
    if (this.args.data.displayValue) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.data.displayValue;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return segmentationValueFormatter(
      // @ts-expect-error: Incorrect type
      this.args.data.segmentationId,
      // @ts-expect-error: Incorrect type
      this.args.data.segmentationValue,
      { intl: this.intl },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipValues() {
    // @ts-expect-error: Incorrect type
    return roundSegmentationValue(this.args.data, false);
  }

  <template>
    {{#unless @hideTitle}}
      <PrismTooltipHeader>
        {{this.tooltipTitle}}
      </PrismTooltipHeader>
    {{/unless}}

    <table class="tooltip-revenue-insight">
      <thead>
        <tr>
          {{! @glint-expect-error: Incorrect type }}
          {{#if (eq @kpis.length 1)}}
            <th class="text-200--bold" colspan="3">
              {{this.singleKpiTitle}}
            </th>
          {{else}}
            <th></th>
            <th></th>
            {{#each this.kpiTitles as |kpiTitle|}}
              <th class="text-200--bold">{{kpiTitle}}</th>
            {{/each}}
          {{/if}}
        </tr>
      </thead>

      <tbody>
        <tr data-test-id="tooltip-revenue-this-year">
          <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
            {{! @glint-expect-error: Incorrect type }}
            @kpiCategory={{@kpiCategory}}
            {{! @glint-expect-error: Incorrect type }}
            @kpis={{@kpis}}
            {{! @glint-expect-error: Incorrect type }}
            @metrics={{this.thisYearMetrics}}
            @showPickupLY={{this.showPickupLYinThisYear}}
            {{! @glint-expect-error: Incorrect type }}
            @tooltipValues={{this.tooltipValues}}
            {{! @glint-expect-error: Incorrect type }}
            @valueMetrics={{this.thisYearValueMetrics}}
          />
        </tr>
        {{#if this.canShowDiscrepancyValue}}
          <tr>
            <td class="data-discrepancy text-center" colspan="3">
              {{t
                "revenue_insight.tooltip.data_discrepancy"
                changePercentage=this.formattedDiscrepancyPercentage
                changeValue=this.formattedDiscrepancyValue
              }}
            </td>
          </tr>
        {{/if}}
      </tbody>
      {{#if this.isTargetActive}}
        <tbody>
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              {{! @glint-expect-error: Incorrect type }}
              @kpiCategory={{@kpiCategory}}
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              {{! @glint-expect-error: Incorrect type }}
              @metrics={{this.targetValue}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
        </tbody>
        <tbody>
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              @isChange={{true}}
              {{! @glint-expect-error: Incorrect type }}
              @kpiCategory={{@kpiCategory}}
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              {{! @glint-expect-error: Incorrect type }}
              @metrics={{this.targetChange}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
        </tbody>
        {{#if this.showPickupLY}}
          <tbody>
            <tr>
              <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
                {{! @glint-expect-error: Incorrect type }}
                @kpiCategory={{@kpiCategory}}
                {{! @glint-expect-error: Incorrect type }}
                @kpis={{@kpis}}
                @metrics={{array "pickupLastYear"}}
                {{! @glint-expect-error: Incorrect type }}
                @tooltipValues={{this.tooltipValues}}
              />
            </tr>
          </tbody>
        {{/if}}
      {{else}}
        <tbody>
          <tr data-test-id="tooltip-revenue-last-year">
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              {{! @glint-expect-error: Incorrect type }}
              @kpiCategory={{@kpiCategory}}
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              @metrics={{this.lastYearMetrics}}
              {{! @glint-expect-error: Incorrect type }}
              @section={{@section}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
              @valueMetrics={{this.lastYearValueMetrics}}
            />
          </tr>
          {{#if this.canShowDiscrepancyReferenceValue}}
            <tr>
              <td class="data-discrepancy text-center" colspan="3">
                {{t
                  "revenue_insight.tooltip.data_discrepancy"
                  changePercentage=this.formattedDisceprancyReferencePercentage
                  changeValue=this.formattedDiscrepancyReferenceValue
                }}
              </td>
            </tr>
          {{/if}}
        </tbody>
        {{#if this.showTooltipDifferenceRow}}
          <tbody>
            <tr>
              <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow
                @kpiCategory={{@kpiCategory}}
                @kpis={{@kpis}}
                @metrics={{this.thisYearMetrics}}
                @tooltipValues={{this.tooltipValues}}
              />
            </tr>
          </tbody>
        {{/if}}
      {{/if}}
    </table>

    {{#if
      (and this.isPickUpOrPaceSegmentationItem this.isPickupAndPaceIncomplete)
    }}
      <PrismTooltipFooter>
        <span class="text-200--regular">
          {{t
            "revenue_insight.pickup_and_pace.incomplete"
            integrationDate=this.riSelections.formattedIntegrationDate
          }}
        </span>
      </PrismTooltipFooter>
    {{/if}}
  </template>
}
